import { useCallback, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { PagePopover } from '@traceio-org/ui';

export interface ChecklistOption {
  value: string;
  label: string;
  /** A colour dot before the label (ticket labels). */
  color?: string | null;
}

/**
 * Several choices in Trace UI's menu (MenuSelect's sibling for many values, such as a ticket's labels): the same
 * PagePopover and classes, with menuitemcheckbox items that toggle without closing the menu, so a few labels are set
 * in one go. Arrows move (and wrap), Home and End jump, Space or Enter toggles, Escape closes with focus back on the
 * trigger. The trigger is named by the label and what is chosen ("Labels: ERP, Bug").
 */
export function MenuChecklist({
  label,
  values,
  options,
  onToggle,
  summary,
  open: openProp,
  onOpenChange,
  emptyText = 'None',
}: {
  label: string;
  values: readonly string[];
  options: readonly ChecklistOption[];
  /** One value turned on (true) or off (false). */
  onToggle: (value: string, checked: boolean) => void;
  /** What the trigger shows; the chosen labels by default. */
  summary?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The trigger's words when nothing is chosen. */
  emptyText?: string;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOwnOpen(next);
    onOpenChange?.(next);
  };
  const labelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const focusMenu = useCallback((element: HTMLDivElement | null) => {
    menu.current = element;
    if (!element) return;
    requestAnimationFrame(() => {
      if (menu.current !== element || !element.isConnected) return;
      element.querySelector<HTMLElement>('[role="menuitemcheckbox"]')?.focus({ preventScroll: true });
    });
  }, []);
  const chosen = options.filter((option) => values.includes(option.value));
  const text = chosen.length > 0 ? chosen.map((option) => option.label).join(', ') : emptyText;
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = options.length - 1;
    const next =
      event.key === 'ArrowDown'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowUp'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : -1;
    if (next < 0) return;
    event.preventDefault();
    const items = menu.current?.querySelectorAll<HTMLElement>('[role="menuitemcheckbox"]');
    items?.[next]?.focus({ preventScroll: true });
  };
  return (
    <div className="admin-menu-select">
      <span id={labelId} className="admin-sr-only">
        {label}
      </span>
      <PagePopover
        label={label}
        variant="operations-period"
        align="start"
        open={open}
        onOpenChange={setOpen}
        returnFocus={triggerRef}
        trigger={
          <button
            ref={triggerRef}
            type="button"
            className="admin-menu-select__trigger"
            aria-labelledby={`${labelId} ${labelId}-value`}
            aria-haspopup="menu"
          >
            <span id={`${labelId}-value`}>{summary ?? text}</span>
          </button>
        }
      >
        <div ref={focusMenu} className="trace-operational-stats-period-menu" role="menu" aria-label={label}>
          {options.length === 0 ? (
            <p className="admin-muted admin-menu-empty">Nothing to choose yet.</p>
          ) : null}
          {options.map((option, index) => {
            const checked = values.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className="trace-operational-stats-period-option"
                role="menuitemcheckbox"
                aria-checked={checked}
                tabIndex={index === 0 ? 0 : -1}
                onClick={() => onToggle(option.value, !checked)}
                onKeyDown={(event) => move(event, index)}
              >
                <span className="admin-checklist-option">
                  {option.color ? (
                    <i className="admin-label-dot" style={{ background: option.color }} aria-hidden="true" />
                  ) : null}
                  {option.label}
                </span>
                {checked ? (
                  <svg
                    aria-hidden="true"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                ) : null}
              </button>
            );
          })}
        </div>
      </PagePopover>
    </div>
  );
}
